import { API, toFullUrl } from '../config/api';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { ImageSlideshow } from '../components/ImageSlideshow';
import { MotionTheme } from '../components/MotionTheme';
import { SmokeEmbers } from '../components/SmokeEmbers';
import { EqualizerBars } from '../components/EqualizerBars';
import { usePlayer } from '../context/PlayerContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { usePWAInstall } from '../hooks/usePWAInstall';

// Home sections
import { HomeNav } from '../components/home/HomeNav';
import { HeroSection } from '../components/home/HeroSection';
import { UpcomingSection } from '../components/home/UpcomingSection';
import { MusicSection } from '../components/home/MusicSection';
import { VideosSection } from '../components/home/VideosSection';
import { AboutSection, FeaturedArtistsSection } from '../components/home/AboutSection';
import { BeatsSection } from '../components/home/BeatsSection';
import { FreeMediaSection } from '../components/home/FreeMediaSection';
import { MerchSection } from '../components/home/MerchSection';
import { ThankYouSection } from '../components/home/ThankYouSection';
import { FollowSection } from '../components/home/FollowSection';
import { DonationsSection } from '../components/home/DonationsSection';
import { SubscribeSection } from '../components/home/SubscribeSection';
import { RaffleSection } from '../components/home/RaffleSection';
import { HomeFooter } from '../components/home/HomeFooter';

export default function Home() {
  const [tracks, setTracks] = useState([]);
  const [upcomingTracks, setUpcomingTracks] = useState([]);
  const [, setLoading] = useState(true);
  const { currentTrack } = usePlayer();
  const { totalItems, setIsOpen: setCartOpen } = useCart();
  const navigate = useNavigate();

  // Pre-save modal state
  const [presaveDialogOpen, setPresaveDialogOpen] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [presaveEmail, setPresaveEmail] = useState('');
  const [presaveName, setPresaveName] = useState('');
  const [presaving, setPresaving] = useState(false);

  // PWA install
  const { canInstall, isInstalled, promptInstall } = usePWAInstall();
  const [showInstallBanner, setShowInstallBanner] = useState(true);

  // Site data
  const [siteSettings, setSiteSettings] = useState(null);
  const [artistProfile, setArtistProfile] = useState(null);
  const [merchItems, setMerchItems] = useState([]);
  const [beats, setBeats] = useState([]);
  const [videos, setVideos] = useState([]);
  const [freeMedia, setFreeMedia] = useState([]);
  const [featuredArtists, setFeaturedArtists] = useState([]);

  useEffect(() => {
    fetchTracks();
    fetchSiteData();
  }, []);

  const fetchSiteData = async () => {
    try {
      const [settingsRes, profileRes, merchRes, beatsRes, videosRes, freeMediaRes, featuredRes] = await Promise.all([
        axios.get(`${API}/site-settings`).catch(() => ({ data: null })),
        axios.get(`${API}/artist-profile/public`).catch(() => ({ data: null })),
        axios.get(`${API}/merch`).catch(() => ({ data: [] })),
        axios.get(`${API}/beats`).catch(() => ({ data: [] })),
        axios.get(`${API}/videos`).catch(() => ({ data: [] })),
        axios.get(`${API}/free-media`).catch(() => ({ data: [] })),
        axios.get(`${API}/featured-artists?homepage=true`).catch(() => ({ data: [] }))
      ]);
      if (settingsRes.data) setSiteSettings(settingsRes.data);
      if (profileRes.data) setArtistProfile(profileRes.data);
      setMerchItems(merchRes.data);
      setBeats(beatsRes.data);
      setVideos(videosRes.data);
      setFreeMedia(freeMediaRes.data);
      setFeaturedArtists(featuredRes.data || []);
    } catch (e) {
      console.error('Failed to fetch site data', e);
    }
  };

  const fetchTracks = async () => {
    try {
      const [tracksRes, upcomingRes] = await Promise.all([
        axios.get(`${API}/tracks`),
        axios.get(`${API}/tracks/upcoming`)
      ]);
      setTracks(tracksRes.data.filter(t => !t.is_upcoming));
      setUpcomingTracks(upcomingRes.data);
    } catch (e) {
      console.error('Failed to fetch tracks', e);
    } finally {
      setLoading(false);
    }
  };

  const handleMerchBuy = async (item) => {
    try {
      if (item.external_url) {
        window.open(item.external_url, '_blank');
        return;
      }
      const { data } = await axios.post(`${API}/merch/checkout`, { item_id: item.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const handleTrackBuy = async (track) => {
    try {
      const { data } = await axios.post(`${API}/checkout`, { track_id: track.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const handleBeatLicense = async (beat, licenseType) => {
    try {
      const { data } = await axios.post(`${API}/beats/checkout`, { beat_id: beat.id, license_type: licenseType, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const themeSettings = siteSettings?.theme_settings || {};
  const bgScope = themeSettings.scope || 'fullpage';
  const motionTheme = siteSettings?.motion_theme || 'embers';
  const sectionVis = siteSettings?.customize?.sections || {};
  const isVisible = (id) => sectionVis[id] !== false;

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative" data-testid="home-page">
      {bgScope === 'fullpage' && motionTheme !== 'off' && (
        <div className="fixed inset-0 z-0 pointer-events-none">
          <MotionTheme theme={motionTheme} videoUrl={toFullUrl(siteSettings?.motion_video_url)} settings={themeSettings} />
        </div>
      )}

      <div className="relative z-10">
        <Helmet>
          <title>{siteSettings?.og_title || 'DraiMoe — I\'m On My 2nd Life | IOMSL'}</title>
          <meta name="description" content={siteSettings?.og_description || 'Stream, buy & download music, beats & merch. DugOwtBoi Entertainment.'} />
          <meta property="og:title" content={siteSettings?.og_title || 'DraiMoe — I\'m On My 2nd Life | IOMSL'} />
          <meta property="og:description" content={siteSettings?.og_description || 'Stream, buy & download music, beats & merch. DugOwtBoi Entertainment.'} />
          {siteSettings?.og_image && <meta property="og:image" content={siteSettings.og_image.startsWith('http') ? siteSettings.og_image : `https://iomsl.com${siteSettings.og_image}`} />}
          <meta property="og:url" content="https://iomsl.com" />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="IOMSL — DraiMoe" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={siteSettings?.og_title || 'DraiMoe — I\'m On My 2nd Life | IOMSL'} />
          <meta name="twitter:description" content={siteSettings?.og_description || 'Stream, buy & download music, beats & merch. DugOwtBoi Entertainment.'} />
          {siteSettings?.og_image && <meta name="twitter:image" content={siteSettings.og_image.startsWith('http') ? siteSettings.og_image : `https://iomsl.com${siteSettings.og_image}`} />}
        </Helmet>

        {/* Studio Header with optional hero-only background */}
        {isVisible('marquee') && (
        <div className="relative">
          {bgScope === 'hero' && motionTheme !== 'off' && (
            <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
              <MotionTheme theme={motionTheme} videoUrl={toFullUrl(siteSettings?.motion_video_url)} settings={themeSettings} />
            </div>
          )}
          <SmokeEmbers
            studioTitle={siteSettings?.customize?.studio_title || 'DUGOWTBOI ENTERTAINMENT™'}
            studioTitleSize={siteSettings?.customize?.studio_title_size || 40}
            studioTitleGradient={siteSettings?.customize?.studio_title_gradient || ['#FF3B30', '#DAA520', '#FFFFFF', '#DAA520', '#FF3B30']}
            worldText={(() => {
              const c = siteSettings?.customize || {};
              const fallback = c.world_text || 'WORLD';
              if (c.world_sync_track && currentTrack?.title) {
                const prefix = (c.world_track_prefix || '').trim();
                return prefix ? `${prefix} ${currentTrack.title}` : currentTrack.title;
              }
              return fallback;
            })()}
            worldSize={siteSettings?.customize?.world_size || 56}
            worldGradient={siteSettings?.customize?.world_gradient || ['#FF3B30', '#FF6347', '#DAA520', '#FFD700', '#FFFFFF']}
            ekgGradient={siteSettings?.customize?.ekg_gradient || ['#FF3B30', '#DAA520', '#00F0FF']}
            ekgLineWidth={siteSettings?.customize?.ekg_line_width || 2}
            ekgRaysEnabled={siteSettings?.customize?.ekg_rays_enabled !== false}
            ekgRaysColor={siteSettings?.customize?.ekg_rays_color || siteSettings?.customize?.heartbeat_color || '#FF3B30'}
            ekgRaysIntensity={siteSettings?.customize?.ekg_rays_intensity ?? 70}
            ekgSpeed={siteSettings?.ekg_speed || 50}
          />
        </div>
        )}

        <HomeNav
          siteSettings={siteSettings}
          totalItems={totalItems}
          setCartOpen={setCartOpen}
          canInstall={canInstall}
          isInstalled={isInstalled}
          showInstallBanner={showInstallBanner}
          setShowInstallBanner={setShowInstallBanner}
          promptInstall={promptInstall}
        />

        {isVisible('hero') && <HeroSection siteSettings={siteSettings} />}

        <UpcomingSection
          upcomingTracks={upcomingTracks}
          presaveDialogOpen={presaveDialogOpen}
          setPresaveDialogOpen={setPresaveDialogOpen}
          selectedTrack={selectedTrack}
          setSelectedTrack={setSelectedTrack}
          presaveEmail={presaveEmail}
          setPresaveEmail={setPresaveEmail}
          presaveName={presaveName}
          setPresaveName={setPresaveName}
          presaving={presaving}
          setPresaving={setPresaving}
          onPresaveSuccess={fetchTracks}
        />

        {/* Reorderable sections — order defined by siteSettings.customize.section_order */}
        {(() => {
          const DEFAULT_ORDER = ['tracks', 'slideshow', 'videos', 'about', 'featured', 'beats', 'free_media', 'merch', 'thank_you', 'follow', 'donations', 'subscribe', 'raffles'];
          const configured = siteSettings?.customize?.section_order;
          const order = Array.isArray(configured) && configured.length ? configured : DEFAULT_ORDER;
          // Ensure any new sections not yet in saved order still render at the end
          const finalOrder = [...order, ...DEFAULT_ORDER.filter(s => !order.includes(s))];

          // Pick the 6 user-starred items for the homepage (fallback: first 6 in display order)
          const pickHomeFeatured = (items, n = 6) => {
            if (!items || items.length === 0) return items;
            const starred = items
              .filter((i) => i.home_featured)
              .sort((a, b) => (a.home_featured_order ?? 0) - (b.home_featured_order ?? 0));
            return starred.length > 0 ? starred.slice(0, n) : items.slice(0, n);
          };
          const homeTracks = pickHomeFeatured(tracks);
          const homeMerch = pickHomeFeatured(merchItems);
          const homeBeats = pickHomeFeatured(beats);
          const homeFreeMedia = pickHomeFeatured(freeMedia);
          const homeVideos = pickHomeFeatured(videos);
          const homeArtists = pickHomeFeatured(featuredArtists);

          const renderers = {
            tracks: () => <MusicSection tracks={homeTracks} siteSettings={siteSettings} navigate={navigate} onTrackBuy={handleTrackBuy} isVisible={isVisible} />,
            slideshow: () => isVisible('slideshow') ? <ImageSlideshow images={siteSettings?.slideshow_images || []} /> : null,
            videos: () => <VideosSection videos={homeVideos} siteSettings={siteSettings} isVisible={isVisible} />,
            about: () => <AboutSection artistProfile={artistProfile} siteSettings={siteSettings} isVisible={isVisible} />,
            featured: () => <FeaturedArtistsSection featuredArtists={homeArtists} siteSettings={siteSettings} isVisible={isVisible} />,
            beats: () => <BeatsSection beats={homeBeats} siteSettings={siteSettings} onBeatLicense={handleBeatLicense} isVisible={isVisible} />,
            free_media: () => <FreeMediaSection freeMedia={homeFreeMedia} siteSettings={siteSettings} isVisible={isVisible} />,
            merch: () => <MerchSection merchItems={homeMerch} siteSettings={siteSettings} onMerchBuy={handleMerchBuy} isVisible={isVisible} />,
            follow: () => <FollowSection artistProfile={artistProfile} siteSettings={siteSettings} isVisible={isVisible} />,
            donations: () => <DonationsSection siteSettings={siteSettings} isVisible={isVisible} />,
            subscribe: () => <SubscribeSection siteSettings={siteSettings} isVisible={isVisible} />,
            thank_you: () => <ThankYouSection siteSettings={siteSettings} isVisible={isVisible} />,
            raffles: () => <RaffleSection siteSettings={siteSettings} isVisible={isVisible} />,
          };

          return finalOrder.map(id => {
            const r = renderers[id];
            return r ? <div key={id}>{r()}</div> : null;
          });
        })()}

        <EqualizerBars
          colors={siteSettings?.customize?.equalizer_colors || ['#FF3B30', '#DAA520', '#8B0000']}
          barCount={siteSettings?.customize?.equalizer_bar_count || 48}
          barGap={siteSettings?.customize?.equalizer_bar_gap ?? 3}
          speed={siteSettings?.customize?.equalizer_speed || 50}
          glow={siteSettings?.customize?.equalizer_glow ?? 60}
          height={siteSettings?.customize?.equalizer_height || 280}
          peakColor={siteSettings?.customize?.equalizer_peak_color || '#FFFFFF'}
        />

        <HomeFooter />
      </div>
    </div>
  );
}
